/**
 * L'Invoice project translations - Start guide section
 */

const startGuide = {
  subtitle: "Here are some tips and setup tasks to help you get started with invoices",
  allDone: "You've completed all setup tasks!",
  showPreviousTasks: "Show previous tasks",
  tasksLeft: "{{tasksLeft}} tasks left (About {{minutesLeft}} minutes)",
  common: {
    fill: "Fill",
    whatToFill: "What you’ll need to fill:",
    country: "Country",
  },
  profile: {
    name: "Fill your profile",
    title: "Fill your profile to start issuing compliant invoices",
    description:
      "Your profile information is used automatically on invoice PDFs and during tax calculations.",
    bullets: {
      english: {
        label: "Use English for all fields",
        text: "Company name, address, and contact person must be in English (Latin alphabet only).",
      },
      address: {
        label: "Complete your full address",
        text: "Postcode, country, city, street, and house number will appear on invoices.",
      },
      vat: {
        label: "Add your VAT number",
        text: "Required for tax-compliant invoice generation (except USA/VIR, where a Sales Tax Permit Number is needed).",
      },
      contacts: {
        label: "Verify contact details",
        text: "Company email and phone number must be valid and accessible (+XXXXXXXXXXXX format).",
      },
      save: {
        label: "Save changes",
        text: "Click “Save” after filling all required fields to complete your profile.",
      },
    },
    note: {
      title: "Please note:",
      pdf: "Address fields are used directly on invoice PDFs",
      companyName: "Company name changes may require document verification",
      registration: "All information must match your official business registration documents",
    },
  },
  vat: {
    name: "Add your VAT number",
    title: "Add your VAT number to generate tax-compliant invoices",
    description:
      "In Lappa Invoice you can either <b>Add a VAT</b> (if you already have one) or <b>Order a VAT</b> (if you need Lappa to help you register).",
    bullets: {
      location: "<b>Where to find VAT settings:</b> Left menu → <b>Settings</b> → <b>VAT</b> tab",
      haveVat: "<b>If you already have a VAT number:</b> click <b>“Add a VAT”</b>",
      noVat: "<b>If you don’t have a VAT number yet:</b> click <b>“Order a VAT”</b> and follow the flow",
    },
    note: {
      countryText: "where you are VAT-registered",
      vatNumberLabel: "VAT number",
      vatNumberText: "as issued by the tax authority",
      reasonLabel: "Reason for registration",
      reasonText: "e.g. Distance sales",
      validity: "<b>Valid from</b> (and <b>Valid till</b> if applicable)",
      footer:
        "Save with <b>“Add registration info”</b> or exit with <b>“Cancel”</b>. After saving, the number appears in the VAT registrations list and can be marked as <b>Active</b>.",
    },
    docLink: {
      text: "Need more details?",
      label: "Read the full VAT setup guide",
    },
    alreadyHave: "I already have a VAT",
  },
  peppol: {
    name: "Add your PEPPOL number",
    title: "Add your Peppol ID to send e-invoices via Peppol",
    description:
      "In L’Invoice you can either <b>Add an existing Peppol ID</b> or <b>Register a new one</b> if you don’t have it yet.",
    bullets: {
      location: "<b>Where to find Peppol settings:</b> Left menu → <b>Settings</b> → <b>PEPPOL</b>",
      havePeppol: "<b>If you already have a Peppol ID:</b> click <b>“Add PEPPOL Number”</b>",
      noPeppol: "<b>If you don’t have one:</b> start the <b>Peppol registration</b> flow",
    },
    note: {
      title: "For existing Peppol ID:",
      countryText: "registration country",
      peppolId: "<b>Peppol ID</b> — must include a colon <b>:</b>",
      footer:
        "After submission, the Peppol ID is sent to a manager for review. Only IDs marked as <b>Approved</b> (green check ✅) can be used.",
    },
    docLink: {
      text: "Need full instructions?",
      label: "Read the complete Peppol setup guide",
    },
    alreadyHave: "I already have a Peppol ID",
  },
  bank: {
    name: "Add bank information",
    title: "Add bank details for PEPPOL invoices",
    description:
      "Bank details are used in PEPPOL documents and appear in the invoice payment section.",
    bullets: {
      location: "<b>Where to find bank settings:</b> Left menu → <b>Settings</b> → <b>Bank details</b>",
      addAccount: "Click <b>“Add bank account”</b> to add a new account",
      currency: "Select the <b>currency</b> and choose <b>Local</b> or <b>Global</b> type",
    },
    note: {
      bankName: "<b>Bank name</b>",
      accountHolder: "<b>Account holder</b>",
      accountDetailsLabel: "Account details",
      accountDetailsText: "IBAN / Account number (depends on currency and type)",
      bicLabel: "BIC / SWIFT",
      bicText: "required for most Global accounts",
      footer:
        "After saving, the account appears as a card. You can mark one account as <b>Default</b> — it will be used first when generating PEPPOL documents.",
    },
    selection: {
      title: "How bank details are selected automatically:",
      currency: "The system matches the <b>invoice currency</b>",
      local: "<b>Local</b> is used if buyer and seller are in the same country",
      global: "<b>Global</b> is used if countries differ",
    },
    docLink: {
      text: "Need all supported currencies and required fields?",
      label: "Read the full bank details guide",
    },
  },
};

export default startGuide;
